import { ArchivedCardsView } from './ArchivedCardsView';
import { ArchivedListsView } from './ArchivedListsView';
import { stopPropagationAndPreventDefault } from 'app/src/stopPropagationAndPreventDefault';
import { View, Slot } from 'app/scripts/views/internal/view';
import { l } from 'app/scripts/lib/localize';
import React from 'react';
import { Disposer, renderComponent } from 'app/src/components/ComponentWrapper';
import { BoardArchive } from 'app/scripts/views/templates/BoardArchive';

interface EmptyViewOptions {}
export class ArchiveView extends View<EmptyViewOptions> {
  // Assigned during `initialize()` (can technically still be undefined during `preinitialize`)
  activeSubview: Slot<unknown>;
  cardsView: ArchivedCardsView;
  listsView: ArchivedListsView;
  searchText: Slot<string>;
  // Assigned when rendering (not guaranteed to be defined)
  $searchInput?: JQuery;
  unmountBoardArchive?: Disposer;

  className = 'board-menu-content-frame';

  events = {
    'click .js-switch-section': 'didClickSwitchSections',
    'input .js-search': 'searchChanged',
  };

  viewTitleKey = 'archive';

  constructor(options: EmptyViewOptions) {
    super(options);
    /**
     * Initialize these in the constructor instead of an `initialize()` method so they aren't
     * overridden by default behavior in `@babel/preset-typescript` to initialize fields as
     * `undefined` in a class's constructor. Our babel configuration does not (yet) allow
     * for the `declare` field prefix, which can be used to skip the initialization:
     *
     * https://babeljs.io/docs/en/babel-preset-typescript#allowdeclarefields
     *
     * This code will run after the generated `_defineProperty` statements, while code in
     * `initialize()` would be run before those.
     */
    this.searchText = this.slot('');

    this.cardsView = this.subview(ArchivedCardsView, this.model, {
      searchText: this.searchText,
      // @ts-expect-error ArchivedCardsView is still a JS file so TS doesn't know it extends View
    }).render();
    this.listsView = this.subview(ArchivedListsView, this.model, {
      searchText: this.searchText,
      // @ts-expect-error ArchivedListsView is still a JS file so TS doesn't know it extends View
    }).render();

    this.activeSubview = this.slot(this.cardsView);
  }

  searchChanged() {
    const val = this.$searchInput?.val() as string | undefined;
    return this.searchText.set(val?.trim() || '');
  }

  didClickSwitchSections(e: MouseEvent) {
    const subview = this.activeSubview.get();

    this.activeSubview.set(
      subview === this.cardsView ? this.listsView : this.cardsView,
    );

    this.$searchInput?.focus();
    // We have to stop this, or else the
    // button will steal keyboard focus
    return stopPropagationAndPreventDefault(e);
  }

  injectSubview(subview: ArchivedCardsView | ArchivedListsView) {
    this.$('.js-archive-content').append(subview.el);
    return subview.didBecomeActive();
  }

  remove() {
    this.unmountBoardArchive && this.unmountBoardArchive();
    return super.remove(...arguments);
  }

  renderOnce() {
    if (!this.unmountBoardArchive) {
      this.unmountBoardArchive = renderComponent(<BoardArchive />, this.$el[0]);
    }

    this.$searchInput = this.$('.js-search');

    this.subscribeChanges(this.activeSubview, {
      in: this.injectSubview,
      out(view: View<EmptyViewOptions>) {
        return view.$el.detach();
      },
    });

    this.watch('activeSubview', (newSubview) => {
      const key =
        newSubview === this.cardsView
          ? 'switch to archived lists'
          : 'switch to archived cards';
      return this.$('.js-switch-section').text(l(key));
    });

    this.watch('activeSubview.isLoading', (isLoading: boolean) => {
      this.$('.js-loading-spinner').toggleClass('hide', !isLoading);
      return this.$('.js-archive-content').toggleClass('hide', isLoading);
    });

    return this;
  }
}
